'use strict';

define('vb/private/services/definition/definitionObject',[
  'vb/private/log',
  'vb/private/constants',
  'vb/private/utils',
  'vb/private/services/serviceConstants',
  'vb/private/services/serviceUtils',
], (
  Log,
  Constants,
  Utils,
  ServiceConstants,
  ServiceUtils,
) => {
  /**
   * @typedef {import('vb/private/services/definition/serviceDefinition').default} ServiceDefinition
   */

  /**
   * Map of transform names to transform functions for one transform category.
   *
   * @typedef {Object.<string, Function>} TransformMap
   */

  /**
   * Runtime transform functions grouped by the service transform category that invokes them.
   *
   * @typedef {Object} Transforms
   * @property {TransformMap} request
   * @property {TransformMap} response
   * @property {TransformMap} metadata
   */

  const logger = Log.getLogger('/vb/private/services/services');

  // names for x-vb extensions
  const VB_TRANSFORMS = 'transforms';
  const VB_TRANSFORMS_PATH = 'path';
  const VB_TRANSFORMS_ORIGIN = 'origin';
  const VB_DISABLED = 'disabled';

  const COMMON_TRANSFORM_NAMES = {
    request: ['vbPrepare', 'sort', 'filter', 'query', 'paginate'],
    response: ['paginate'],
    metadata: ['capabilities'],
  };

  /**
   * Reports legacy inline transform bodies.
   * Inline transform code is ignored because service definitions can cross a network trust boundary.
   * Use the optional 'transforms.path' extension to load transform modules instead.
   *
   * @param {Object} [transformsDef]
   */
  function validateNoInlineTransformCode(transformsDef = {}) {
    Object.keys(COMMON_TRANSFORM_NAMES).forEach((key) => {
      if (transformsDef[key]) {
        Object.keys(transformsDef[key]).forEach((xformKey) => {
          if (transformsDef[key][xformKey]) {
            logger.warn('Ignoring inline x-vb transform', key, xformKey,
              '-- use transforms.path instead.');
          }
        });
      }
    });
  }

  // Class hierarchy:
  //
  // DefinitionObject
  //   -> PluginServiceDefinition
  //   -> OpenApiDefinitionObject
  //        -> ServiceDefinition
  //        -> Endpoint

  /**
   * a base class for Service and Endpoint, for assigning private properties based on the 'x-vb' extension.
   * provides a load() method for asynchronously loading anything required by the extension
   *
   * about 'parent':
   * Extensions ("x-vb") are really only realized on the Endpoints; the Service has properties for the extensions
   * defined at the service level, but its really only through inheritance in the Endpoint that the extensions
   * are applied.  Meaning, the Endpoint '_extensions' represent the merge of extensions defined both
   * on the Service and Endpoint.
   */
  class DefinitionObject {
    /**
     * @param {string} name
     * @param {Object} extensions the 'pure' swagger/openapi3 model extensions
     * @param {ServiceDefinition} parent for ServiceDefinition, this is null.
     *                      For Endpoint, this is the ServiceDefinition, to allow extension inheritance.
     * @param {string} namespace
     * @param {string} relativePath container's path
     * @param catalogInfo possible additions/overrides, local to the app
     * @param {boolean} isUnrestrictedRelative true means, allow using parent folders in paths (only Application)
     */
    constructor(name, extensions, parent, namespace, relativePath, catalogInfo, isUnrestrictedRelative) {
      this._name = name;
      // extensions metadata; the 'x-vb' object from the JSON, merged with anything from the catalog
      // ignore anything in the 'catalogInfo.services'; that's only used for getting the service def, itself
      const backendExtensions = (catalogInfo && catalogInfo.backends && catalogInfo.backends.extensions) || {};

      // First, flatten (merge) the x-vb info ext
      //
      // merge the catalog.json with the x-vb for this object.
      // If this object is an endpoint, the x-vb has already been flattened with its parent serviceDev.
      // eslint-disable-next-line no-underscore-dangle
      this._extensions = DefinitionObject._mergeExtensions(extensions, backendExtensions);

      this._loadExtensionsPromise = null;
      this._relativePath = relativePath || '';

      this._catalogInfo = catalogInfo || { chain: [] };

      this._isUnrestrictedRelative = isUnrestrictedRelative;
      this._transformsDef = undefined;

      this._parent = parent;
      this._namespace = namespace;
    }

    get name() {
      return this._name;
    }

    get namespace() {
      return this._namespace;
    }

    get extensions() {
      return this._extensions;
    }

    set extensions(extensions) {
      this._extensions = extensions || {};
    }

    get relativePath() {
      return this._relativePath;
    }

    get isUnrestrictedRelative() {
      return this._isUnrestrictedRelative;
    }

    get catalogInfo() {
      return this._catalogInfo;
    }

    set catalogInfo(catalogInfo) {
      this._catalogInfo = catalogInfo;
    }

    /**
     * Sets up transforms related properties on the first get.
     * Do not call this inside the constructor to allow subclasses to inject their own data in the extensions.
     */
    _initTransforms() {
      this._transformsDef = this.extensions[VB_TRANSFORMS] || {};

      validateNoInlineTransformCode(this._transformsDef);

      // Set the transform maps now. Obsolete Inline transform bodies are only validated above;
      // module transforms are loaded and parent transforms are merged in load().
      /** @type {Transforms} */
      this._transforms = {
        request: {},
        response: {},
        metadata: {},
      };

      // merge in the parent ones when we filter
      this._disabledTransforms = this._transformsDef[VB_DISABLED] || {};
    }

    /**
     * Runtime transform functions grouped by request, response, and metadata category.
     *
     * @returns {Transforms}
     */
    get transforms() {
      if (!this._transforms) {
        this._initTransforms();
      }
      return /** @type {Transforms} */ (this._transforms);
    }

    getRequestTransforms() {
      return this.transforms.request;
    }

    getResponseTransforms() {
      return this.transforms.response;
    }

    getMetadataTransforms() {
      return this.transforms.metadata;
    }

    get disabledTransforms() {
      if (!this._disabledTransforms) {
        this._initTransforms();
      }
      return this._disabledTransforms;
    }

    /**
     * Object containing reference to the transforms file.
     * If the transforms file was declared separately from the service object
     * this definition will contain that information in the origin property.
     *
     * @returns { { path: string, [origin: Object] }}
     */
    get transformsDef() {
      if (!this._transformsDef) {
        this._initTransforms();
      }
      return this._transformsDef;
    }

    /**
     *
     * @param {string} filename
     * @returns
     */
    resolveTransformsPath(filename) {
      return filename ? ServiceUtils.resolveResourcePath(filename, this.relativePath, false) : '';
    }

    /**
     * first time called, will check for the 'transforms.path' extension, and initialize
     * transformations from the JS module.
     * With or without a transforms.path, the Promise is resolved with 'this'.
     * Subsequent calls return the previous promise.
     * @returns {Promise} resolved with 'this'
     */
    load() {
      // make sure the parent has been loaded already
      return Promise.resolve(this._parent && this._parent.load())
        .then(() => {
          this._loadExtensionsPromise = this._loadExtensionsPromise
            || this._loadTransformsAndMerge(this.transformsDef).then(() => this);

          return this._loadExtensionsPromise;
        });
    }

    _setRequestTransforms(...requestMaps) {
      Object.assign(this.transforms.request, ...requestMaps);
    }

    _setResponseTransforms(...responseMaps) {
      Object.assign(this.transforms.response, ...responseMaps);
    }

    _setMetadataTransforms(...metadataMaps) {
      Object.assign(this.transforms.metadata, ...metadataMaps);
    }

    /**
     * @param pathJs if path is false, this returns an empty resolved promise.
     * Otherwise, calls Utils.getResources.
     *
     * NOTE: this uses requireJS to load the script because they are JavaScript, and require modules.
     * If we use fetch(), we need to execute those somehow.
     *
     * @returns {Promise}
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    _loadTransformModule(pathJs) {
      return Promise.resolve()
        .then(() => {
          const uri = ServiceUtils.getModuleUri(pathJs);
          if (uri) {
            return Utils.getRuntimeEnvironment()
              .then((rtEnv) => rtEnv.getTransformsSource(uri.toString()));
          }

          return {};
        });
    }

    /**
     * 1) load our JS transforms, if any
     * 2) merge the parents transforms, our existing inline transforms, and any JS transforms
     *
     * Before calling this, make sure parent.load() has been called, so its transforms are ready to be inherited.
     *
     * @param {Object} [transformsDef]
     * @param {String} [transformsDef.path]
     * @param {Object} [transformsDef.origin]
     * @returns {Promise}
     */
    _loadTransformsAndMerge(transformsDef = {}) {
      let path = transformsDef[VB_TRANSFORMS_PATH];

      // origin of the transforms declaration
      // this is available only for transforms defined in catalogs on the backends
      const txOrigin = transformsDef[VB_TRANSFORMS_ORIGIN];

      /** @type {Transforms} */
      const transforms = {
        request: {},
        response: {},
        metadata: {},
      };

      return Promise.resolve()
        .then(() => {
          // need to figure out if its relative to service, or application
          // two cases that might come form design-time, initially:
          // -- './transform.js' - relative to SERVICE def file
          // -- 'vb/ServiceRampTransforms' - relative to normal requireJS base URI

          if (path) {
            // do not allow Flows to use '..' paths
            if (path.indexOf(Constants.PARENT_FOLDER) >= 0 && (!txOrigin && !this.isUnrestrictedRelative)) {
              logger.error('found', Constants.PARENT_FOLDER,
                'in Flow transform path, which is not allowed. skipping:', path);
              return transforms;
            }

            // if the transform starts with the RELATIVE_FOLDER_PREFIX (a dot),
            // it means we want to interpret the path as relative to the service def.
            // in that case, add the service path prefix to the transform path
            if (path.startsWith(Constants.RELATIVE_FOLDER_PREFIX)) {
              // 'this' is either an Endpoint or a ServiceDefinition
              // either our parent has a filename (parent is a Service), or we do (we are a Service)
              // in case the transforms metadata is inherited from a backend (i.e. has transformsDef.origin)
              // we need to use its path to resolve the transforms path
              const servicePath = (txOrigin && txOrigin.path)
                  || (this._parent && this._parent.catalogInfo && this._parent.catalogInfo.url)
                  || (this.catalogInfo && this.catalogInfo.url);

              // use the service path for the transform path
              if (servicePath) {
                // get the path part of the service def path, without the file name
                const index = servicePath.lastIndexOf(Constants.PATH_SEPARATOR);
                const prefix = (index <= 0) ? '' : (servicePath.substring(0, index + 1));
                // and prepend it to the transform file path (including transform file name)
                path = `${prefix}${path}`;
              }
            } else if (this.isUnrestrictedRelative && !Utils.isAbsolutePath(path)) {
              // if there is no dot, we want this to be app-relative, not container relative
              path = this.resolveTransformsPath(path);
            }
          }

          return this._loadTransformModule(path);
        })
        .then((module) => {
          // the module should return an object with this form; there is no restriction on transform name,
          // since restAction, and the Rest helper, simply apply all transforms.
          // but standard names should be used since they have meaning in other contexts, like ServiceDataProvider
          /**
           * {
           *  request: {
           *    sort: fn(),
           *    paginate: fn(),
           *    ...
           *  },
           *  response: {
           *   paginate: fn()
           *   ...
           *  }
           */
          Object.keys(module).forEach((category) => {
            // each property of the module object can either be a constructor or a singleton
            const instance = (typeof module[category] === 'function') ? new module[category]() : module[category];

            // walk prototype chain, get properties of the instance, and its prototype (not the chain).
            const propertyNames = [];
            propertyNames.push(...Object.getOwnPropertyNames(instance));
            let proto = Object.getPrototypeOf(instance);
            const protoObj = Object.getPrototypeOf({});
            while (proto && proto !== protoObj) {
              propertyNames.push(...Object.getOwnPropertyNames(Object.getPrototypeOf(instance)));
              proto = Object.getPrototypeOf(proto);
            }
            // use default names to make sure we look for the common ones, regardless of module structure
            COMMON_TRANSFORM_NAMES[category].forEach((commonName) => {
              if (propertyNames.indexOf(commonName) === -1) {
                propertyNames.push(commonName);
              }
            });

            // filter '_' and constructor
            propertyNames.filter((name) => name !== 'constructor' && name[0] !== '_').forEach((functionName) => {
              if (instance[functionName] && typeof instance[functionName] === 'function') {
                transforms[category][functionName] = instance[functionName].bind(instance);
                // this boolean is our clue that this transform fnc one of ours, and will do its own transforms
                // so we need to copy the value over, after the bind()
                if (instance[functionName].doesQueryEncoding) {
                  transforms[category][functionName].doesQueryEncoding = true;
                }
              }
            });
          });

          // here we finally inherit transforms from our parent.  precedence is, from lowest to highest:
          // - all parent transforms, if any
          // - our own JS module transforms

          const parentTransforms = /** @type {Transforms} */ ((this._parent && this._parent.transforms) || {});

          this._setRequestTransforms(parentTransforms.request, transforms.request);
          this._setResponseTransforms(parentTransforms.response, transforms.response);
          this._setMetadataTransforms(parentTransforms.metadata, transforms.metadata);

          return transforms;
        });
    }

    /**
     * merge the headers; otherwise, catalog overrides the definition
     * @param {Object} openApiExt extensions from OpenApi
     * @param {Object} catalogExt extensions from catalog
     * @returns {Object}
     * @private
     */
    static _mergeExtensions(openApiExt = {}, catalogExt = {}) {
      // for headers, the catalog will override anything in the servers
      // @todo: should headers in catalog override the service def?
      const headers = Object.assign({}, openApiExt.headers || {}, catalogExt.headers || {});

      // otherwise, the service def takes precedence over catalog (auth block, etc).

      // take the (atomic) auth block from either the openapi3, or the catalog
      const authentication = Object.assign({}, openApiExt.authentication || catalogExt.authentication || {});

      const merged = Object.assign({}, catalogExt, openApiExt, { headers });
      // don't add an empty 'authorization' object, just to keep it clean-ish, and keep the 'clutter' low
      if (Object.keys(authentication).length) {
        merged[ServiceConstants.AUTH_DECL_NAME] = authentication;
      }
      return merged;
    }
  }

  return DefinitionObject;
});

